import { useCustom } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  ExternalLink,
  Tag as TagIcon,
  User as UserIcon,
  BookOpen,
  GraduationCap,
  Package,
  FileText,
  Radio,
  Send,
  Ticket,
  CreditCard,
  Gem,
  Image as ImageIcon,
  Megaphone,
  type LucideIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { resolveApiUrl } from '@/providers/api-url';
import type { TagTargetType } from '@masters-club/types';

interface AssignmentItem {
  id: number;
  name: string;
  subtitle: string | null;
  /**
   * id родительской сущности — для типов без собственного маршрута
   * редактирования (MODULE / MODULE_ITEM редактируются inline в курсе).
   * Для остальных типов — null.
   */
  parentId: number | null;
}

interface AssignmentGroup {
  targetType: TagTargetType;
  items: AssignmentItem[];
}

interface Props {
  tagId: number;
}

/**
 * Способ построения маршрута редактирования сущности из карточки:
 * - 'self'   — у сущности свой маршрут `<base>/<id>` (большинство типов).
 * - 'parent' — собственного маршрута нет, ведём на родителя `<base>/<parentId>/...`
 *              (модуль и урок открываются внутри курса).
 * - 'none'   — редактирование из карточки недоступно.
 */
type RouteKind =
  | { mode: 'self'; prefix: string }
  | { mode: 'parent'; build: (parentId: number) => string }
  | { mode: 'none' };

const TARGET_TYPE_META: Record<TagTargetType, {
  label: string;
  route: RouteKind;
  icon: LucideIcon;
}> = {
  USER: { label: 'Пользователи', route: { mode: 'self', prefix: '/users/show' }, icon: UserIcon },
  BOOK: { label: 'Книги', route: { mode: 'self', prefix: '/books/edit' }, icon: BookOpen },
  COURSE: { label: 'Курсы', route: { mode: 'self', prefix: '/courses/edit' }, icon: GraduationCap },
  MODULE: {
    label: 'Модули',
    route: { mode: 'parent', build: (courseId) => `/courses/${courseId}/modules` },
    icon: Package,
  },
  MODULE_ITEM: {
    label: 'Уроки',
    route: { mode: 'parent', build: (courseId) => `/courses/${courseId}/modules` },
    icon: FileText,
  },
  LIVESTREAM: { label: 'Трансляции', route: { mode: 'self', prefix: '/livestreams/edit' }, icon: Radio },
  TELEGRAM_CHANNEL: { label: 'Telegram-каналы', route: { mode: 'self', prefix: '/telegram-channels/edit' }, icon: Send },
  CHANNEL_PASS: { label: 'Подписки в канал', route: { mode: 'self', prefix: '/channel-passes/edit' }, icon: Ticket },
  SUBSCRIPTION_PLAN: { label: 'Абонементы', route: { mode: 'self', prefix: '/subscription-plans/edit' }, icon: CreditCard },
  PRICING_TIER: { label: 'Тарифы', route: { mode: 'none' }, icon: Gem },
  BANNER: { label: 'Баннеры', route: { mode: 'self', prefix: '/banners/edit' }, icon: ImageIcon },
  MINI_BANNER: { label: 'Мини-баннеры', route: { mode: 'self', prefix: '/mini-banners/edit' }, icon: Megaphone },
  PROMO_ANNOUNCEMENT: { label: 'Объявления', route: { mode: 'self', prefix: '/announcements/edit' }, icon: Megaphone },
};

/**
 * Вычисляет путь редактирования для записи карточки.
 * Возвращает null, если маршрут построить нельзя (mode='none' или
 * отсутствует parentId для типа, требующего родителя).
 */
function resolveEditPath(route: RouteKind, item: AssignmentItem): string | null {
  switch (route.mode) {
    case 'self':
      return `${route.prefix}/${item.id}`;
    case 'parent':
      return item.parentId !== null ? route.build(item.parentId) : null;
    case 'none':
      return null;
  }
}

/**
 * Сетка карточек: по одной на тип сущности, к которой прикреплён тег.
 * Внутри карточки — счётчик и список с tooltip-превью и ссылкой на редактирование.
 */
export function TagAssignmentsCards({ tagId }: Props) {
  const navigate = useNavigate();
  const { query } = useCustom<AssignmentGroup[]>({
    url: `${resolveApiUrl()}/tags/${tagId}/assignments`,
    method: 'get',
    queryOptions: {
      staleTime: 0,
      refetchOnMount: 'always',
    },
  });

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
        Загрузка...
      </div>
    );
  }

  const groupsRaw = query.data?.data;
  const groups: AssignmentGroup[] = Array.isArray(groupsRaw) ? groupsRaw : [];

  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground py-10 border border-dashed rounded-lg">
        <TagIcon className="h-8 w-8 opacity-40" />
        <span>Тег ни к чему не прикреплён</span>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {groups.map((group) => {
        const meta = TARGET_TYPE_META[group.targetType];
        if (!meta) return null;
        const Icon = meta.icon;
        return (
          <Card key={group.targetType} className="overflow-hidden">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
                  {meta.label}
                </span>
                <Badge variant="secondary" className="font-mono">
                  {group.items.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {group.items.map((item) => {
                  const editPath = resolveEditPath(meta.route, item);
                  const button = (
                    <button
                      type="button"
                      onClick={editPath ? () => navigate(editPath) : undefined}
                      disabled={editPath === null}
                      className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-accent disabled:cursor-default disabled:hover:bg-transparent flex items-center justify-between gap-2 group"
                    >
                      <span className="flex flex-col min-w-0">
                        <span className="truncate font-medium">{item.name}</span>
                        {item.subtitle && (
                          <span className="truncate text-xs text-muted-foreground">{item.subtitle}</span>
                        )}
                      </span>
                      {editPath && (
                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                      )}
                    </button>
                  );
                  return (
                    <li key={item.id}>
                      <Tooltip>
                        <TooltipTrigger asChild>{button}</TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <div className="space-y-1">
                            <div className="font-medium">{item.name}</div>
                            {item.subtitle && (
                              <div className="text-xs text-muted-foreground">{item.subtitle}</div>
                            )}
                            <div className="text-[11px] text-muted-foreground pt-1">
                              {editPath ? 'Нажмите для редактирования' : 'Редактирование недоступно'}
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
